import { z } from 'zod';
import { CreativeType } from '@/shared/types/enums';

const CTA_VALUES = [
  'BUY_TICKETS',
  'LEARN_MORE',
  'SIGN_UP',
  'REGISTER',
  'GET_TICKETS',
  'BOOK_NOW',
  'CONTACT_US',
  'APPLY_NOW',
] as const;

// prompt/model are AI-generation metadata — a manually uploaded image
// legitimately has neither, so they must be optional or the upload response
// fails validation and blocks the step.
const generatedImageSchema = z.object({
  imageUrl: z.string().url(),
  imageStorageKey: z.string().min(1),
  prompt: z.string().optional(),
  mimeType: z.string().min(1),
  model: z.string().optional(),
  expiresAt: z.string().min(1),
  // Links the entry back to the picked file so uploader removal can drop it.
  sourceFileFingerprint: z.string().optional(),
});

const wizardCreativeSchema = z
  .object({
    type: z.nativeEnum(CreativeType, {
      errorMap: () => ({ message: 'Please select a creative type' }),
    }),
    // Items are NOT individually required. The wizard pairs headlines with
    // descriptions into "Variation N" cards, so adding a card creates an empty
    // slot in whichever pool the user hasn't typed into yet — a per-item
    // `min(1)` would block Continue on a field nobody touched. Empty slots are
    // meaningless in a pooled asset model and `mapWizardPayloadToCreateCampaign`
    // already trims and filters them out before dispatch. The "at least one
    // non-empty" rule is enforced by the superRefine below instead.
    headlines: z
      .array(z.string().max(90, 'Headline must be 90 characters or less'))
      .min(1, 'At least one headline is required'),
    descriptions: z.array(z.string().max(300, 'Description must be 300 characters or less')),
    callToAction: z.enum(CTA_VALUES, {
      errorMap: () => ({ message: 'Please select a call to action' }),
    }),
    mediaFiles: z.array(z.instanceof(File)).optional(),
    mediaPreviews: z.array(z.string()).optional(),
    generatedImages: z.array(generatedImageSchema).optional(),
    // Square (1:1) slot — Google Display only.
    squareMediaFiles: z.array(z.instanceof(File)).optional(),
    squareMediaPreviews: z.array(z.string()).optional(),
    squareGeneratedImages: z.array(generatedImageSchema).optional(),
    // Video slot — VIDEO creatives only.
    videoFile: z.instanceof(File).optional(),
    videoPreview: z.string().optional(),
    videoStorageKey: z.string().optional(),
    // Cover image (thumbnail) for VIDEO creatives — Meta requires one.
    videoThumbnailFile: z.instanceof(File).optional(),
    videoThumbnailPreview: z.string().optional(),
    videoThumbnailStorageKey: z.string().optional(),
    // YouTube URL — Google VIDEO creatives only (Google references a YouTube
    // video by id rather than uploaded bytes).
    youtubeVideoUrl: z.string().url('Please enter a valid YouTube URL').optional(),
    // Carousel cards — CAROUSEL creatives only.
    carouselCards: z
      .array(
        z.object({
          imageStorageKey: z.string().optional(),
          imageUrl: z.string().optional(),
          headline: z.string().max(90, 'Headline must be 90 characters or less').optional(),
          description: z.string().max(300, 'Description must be 300 characters or less').optional(),
          destinationUrl: z.string().optional(),
        }),
      )
      .optional(),
  })
  .superRefine((creative, ctx) => {
    // Replaces the per-item `min(1)` on headlines: at least one slot must carry
    // real text, but any individual slot may sit empty while the user fills the
    // variation cards. Attached to the array path so AdCopyFields can render it
    // once under the card list.
    if (!creative.headlines.some((headline) => headline.trim().length > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['headlines'],
        message: 'Add at least one headline',
      });
    }
  });

/** Meta post ids are `{page-id}_{post-id}`, both numeric. */
const BOOST_POST_ID_PATTERN = /^\d+_\d+$/;

export const creativeSchema = z.object({
  creatives: z.array(wizardCreativeSchema).min(1, 'At least one creative is required'),
  destinationUrl: z.string().min(1, 'Destination URL is required').url('Please enter a valid URL'),
  boostPostId: z.string().optional(),
});

export type CreativeFormValues = z.infer<typeof creativeSchema>;

type WizardCreativeValues = z.infer<typeof wizardCreativeSchema>;

/**
 * Validation for the "boost an existing post" mode.
 *
 * The ad reuses a post the Page already published, so there is no headline,
 * image or destination URL to check — only which post. The creative fields
 * still ride along untouched so switching back to the normal mode restores
 * whatever the organizer had already typed.
 */
export const boostCreativeSchema = z.object({
  creatives: z.array(z.custom<WizardCreativeValues>(() => true)),
  destinationUrl: z.string(),
  boostPostId: z.string().regex(BOOST_POST_ID_PATTERN, 'Select the post you want to boost'),
});
